'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { Plus, RefreshCw, Smartphone, Users, Wifi, WifiOff } from 'lucide-react'
import type { Account, ConsoleUser, LocalWechatAccount } from '@/types'
import { PageHeader } from '@/components/PageHeader'
import { SectionCard } from '@/components/SectionCard'
import { LazyAvatar } from '@/components/LazyAvatar'
import { apiGet } from '@/lib/api'

type RuntimeAccount = LocalWechatAccount & { runtimeStatus: 'online' | 'offline'; isCurrent?: boolean }
type RuntimeStatusResponse = {
  success: boolean
  mode: 'gateway'
  runtimeSource?: 'gateway' | 'unavailable'
  total: number
  online: number
  offline: number
  onlineWxids: string[]
  accounts: RuntimeAccount[]
}
type DashboardViewProps = {
  account: Account | null
  consoleUser: ConsoleUser
  localAccounts: LocalWechatAccount[]
  localAccountsError: string
  switchAccount: (account: LocalWechatAccount) => void
  onlineWxAccount: (wxid: string) => Promise<void> | void
  downlineWxAccount: (wxid: string) => Promise<void> | void
  removeWxAccount: (wxid: string) => Promise<void> | void
  startAddWechatAccount: () => void
}

type MetricTone = 'ink' | 'green' | 'amber' | 'accent'

const metricToneStyles: Record<MetricTone, string> = {
  ink: 'bg-gray-100 text-soft',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
  accent: 'bg-accent/10 text-accent',
}

function MetricCard({
  title,
  value,
  icon,
  tone = 'ink',
  footer,
}: {
  title: string
  value: string | number
  icon: ReactNode
  tone?: MetricTone
  footer?: ReactNode
}) {
  return (
    <div className="rounded-card border border-hair bg-white p-5 shadow-card transition-shadow hover:shadow-lg">
      <div className="flex items-center gap-4">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${metricToneStyles[tone]}`}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-muted">{title}</div>
          <div className="mt-0.5 truncate text-2xl font-semibold text-ink" title={String(value)}>
            {value}
          </div>
        </div>
      </div>
      {footer ? <div className="mt-4">{footer}</div> : null}
    </div>
  )
}

function RatioBar({ value, total, barClassName }: { value: number; total: number; barClassName: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div className="flex items-center gap-3">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${barClassName}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 text-xs text-muted">{pct}%</span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === 'online' || status === 'active'
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
        ok
          ? 'bg-green-50 text-green-700'
          : status === 'offline'
            ? 'bg-yellow-50 text-yellow-700'
            : 'bg-gray-100 text-gray-600'
      }`}
    >
      {status === 'online' ? '在线' : status === 'offline' ? '离线' : status === 'unknown' ? '未知' : status}
    </span>
  )
}

function statusActionTitle(owned: boolean, runtimeUnknown: boolean): string | undefined {
  if (!owned) return '只能操作当前控制台账号绑定的微信号'
  if (runtimeUnknown) return '微信在线状态暂不可用，请稍后刷新重试'
  return undefined
}

function wechatNo(account: Pick<LocalWechatAccount, 'alias' | 'wxid'> | Account | null | undefined) {
  if (!account) return '-'
  return account.alias || account.wxid || '-'
}

export function DashboardView({
  account,
  consoleUser,
  localAccounts,
  localAccountsError,
  switchAccount,
  onlineWxAccount,
  downlineWxAccount,
  removeWxAccount,
  startAddWechatAccount,
}: DashboardViewProps) {
  const [runtime, setRuntime] = useState<RuntimeStatusResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [runtimeError, setRuntimeError] = useState('')
  const canOperateAccount = (item: LocalWechatAccount) => item.localUserUuid === consoleUser.uuid
  // The gateway answers `runtimeSource: 'unavailable'` with an empty online
  // list — that means "unknown", never "everyone is offline".
  const runtimeUnknown = !!runtimeError || runtime?.runtimeSource === 'unavailable'

  async function refreshRuntime(isStale?: () => boolean) {
    setLoading(true)
    try {
      const query = account?.wxid ? `?currentWxid=${encodeURIComponent(account.wxid)}` : ''
      const runtimeStatus = await apiGet<RuntimeStatusResponse>(`/accounts/runtime-status${query}`, { silent: true })
      // Skip stale responses so a slow request for the previous account can't
      // overwrite the state of the newly selected one.
      if (isStale?.()) return
      setRuntime(runtimeStatus)
      setRuntimeError('')
    } catch (err) {
      if (isStale?.()) return
      setRuntime(null)
      setRuntimeError(err instanceof Error ? err.message : String(err))
    } finally {
      if (!isStale?.()) setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    void refreshRuntime(() => cancelled)
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.wxid, localAccounts.length])

  async function handleOnline(wxid: string) {
    await onlineWxAccount(wxid)
    await refreshRuntime()
  }

  async function handleDownline(wxid: string) {
    await downlineWxAccount(wxid)
    await refreshRuntime()
  }

  async function handleRemove(wxid: string) {
    await removeWxAccount(wxid)
    await refreshRuntime()
  }

  const accounts =
    runtime?.accounts ||
    localAccounts.map((item) => ({
      ...item,
      runtimeStatus: item.wxid === account?.wxid ? ('online' as const) : ('offline' as const),
      isCurrent: item.wxid === account?.wxid && item.localUserUuid === consoleUser.uuid,
    }))
  const total = runtime?.total ?? localAccounts.length
  const online = runtime?.online ?? accounts.filter((item) => item.runtimeStatus === 'online').length
  const offline = runtime?.offline ?? Math.max(total - online, 0)
  const selectedAccount =
    account && accounts.find((item) => item.wxid === account.wxid && item.localUserUuid === consoleUser.uuid)
  const displayAccount = selectedAccount || account

  return (
    <section className="flex h-full flex-col bg-page">
      <PageHeader
        title="控制台"
        description={`${consoleUser.username} · ${consoleUser.role} · ${consoleUser.status}`}
        action={
          <button type="button" className="btn-secondary" onClick={() => void refreshRuntime()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            刷新状态
          </button>
        }
      />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="w-full space-y-5">
          {runtimeUnknown ? (
            <div role="alert" className="rounded-card border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-700">
              微信在线状态暂不可用（网关未响应），下方状态仅为占位，请稍后点击“刷新状态”重试。
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="微信账号总数"
              value={total}
              icon={<Users size={20} />}
              footer={<div className="truncate text-xs text-muted">含所有控制台账号绑定的微信</div>}
            />
            <MetricCard
              title="在线微信"
              value={runtimeUnknown ? '未知' : online}
              icon={<Wifi size={20} />}
              tone="green"
              footer={
                runtimeUnknown ? (
                  <div className="truncate text-xs text-muted">在线状态暂不可用</div>
                ) : (
                  <RatioBar value={online} total={total} barClassName="bg-green-500" />
                )
              }
            />
            <MetricCard
              title="离线微信"
              value={runtimeUnknown ? '未知' : offline}
              icon={<WifiOff size={20} />}
              tone="amber"
              footer={
                runtimeUnknown ? (
                  <div className="truncate text-xs text-muted">在线状态暂不可用</div>
                ) : (
                  <RatioBar value={offline} total={total} barClassName="bg-amber-400" />
                )
              }
            />
            <div className="rounded-card border border-hair bg-white p-5 shadow-card transition-shadow hover:shadow-lg">
              <div className="flex items-center gap-4">
                {displayAccount ? (
                  <LazyAvatar
                    src={displayAccount.avatar}
                    seed={displayAccount.wxid}
                    alt="avatar"
                    className="h-11 w-11 shrink-0 rounded-xl border border-hair bg-white"
                  />
                ) : (
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${metricToneStyles.accent}`}
                  >
                    <Smartphone size={20} />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-muted">当前微信</div>
                  <div
                    className="mt-0.5 truncate text-lg font-semibold text-ink"
                    title={displayAccount?.nickName || displayAccount?.wxid || undefined}
                  >
                    {displayAccount?.nickName || displayAccount?.wxid || '未选择'}
                  </div>
                </div>
              </div>
              <div className="mt-4 truncate text-xs text-muted">
                {displayAccount ? `微信号：${wechatNo(displayAccount)}` : '进入聊天和自动回复前请先选择微信号'}
              </div>
            </div>
          </div>

          <SectionCard
            icon={<Users size={20} />}
            title="微信账号列表"
            padded={false}
            action={
              <button type="button" className="btn-primary" onClick={startAddWechatAccount}>
                <Plus size={16} />
                继续登录新微信
              </button>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-gray-50 text-xs text-muted">
                  <tr>
                    <th className="px-6 py-3 font-medium">微信</th>
                    <th className="px-6 py-3 font-medium">微信号</th>
                    <th className="px-6 py-3 font-medium">绑定控制台账号</th>
                    <th className="px-6 py-3 font-medium">运行状态</th>
                    <th className="px-6 py-3 font-medium">更新时间</th>
                    <th className="px-6 py-3 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hair">
                  {accounts.length ? (
                    accounts.map((item) => {
                      const owned = canOperateAccount(item)
                      return (
                        <tr key={`${item.localUserUuid}:${item.wxid}`}>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <LazyAvatar
                                src={item.avatar}
                                seed={item.wxid}
                                alt="avatar"
                                className="h-9 w-9 rounded border border-hair bg-white"
                              />
                              <div>
                                <div className="font-medium text-ink">{item.nickName || item.wxid}</div>
                                {item.isCurrent ? <div className="text-xs text-accent">当前选中</div> : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-muted" title={item.wxid}>
                            {wechatNo(item)}
                          </td>
                          <td className="px-6 py-3">
                            <div className="text-ink">{item.ownerUsername || item.localUserUuid}</div>
                            {owned ? <div className="text-xs text-accent">当前控制台账号</div> : null}
                          </td>
                          <td className="px-6 py-3">
                            <StatusBadge status={runtimeUnknown ? 'unknown' : item.runtimeStatus} />
                          </td>
                          <td className="px-6 py-3 text-muted">
                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => switchAccount(item)}
                                disabled={!owned || item.wxid === account?.wxid}
                                title={owned ? undefined : '只能操作当前控制台账号绑定的微信号'}
                              >
                                切换
                              </button>
                              {item.runtimeStatus === 'online' ? (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => void handleDownline(item.wxid)}
                                  disabled={!owned || runtimeUnknown}
                                  title={statusActionTitle(owned, runtimeUnknown)}
                                >
                                  下线
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => void handleOnline(item.wxid)}
                                  disabled={!owned || runtimeUnknown}
                                  title={statusActionTitle(owned, runtimeUnknown)}
                                >
                                  上线
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn-danger"
                                onClick={() => void handleRemove(item.wxid)}
                                disabled={!owned}
                                title={owned ? undefined : '只能操作当前控制台账号绑定的微信号'}
                              >
                                下线并移除
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-muted">
                        {localAccountsError
                          ? `微信账号列表加载失败：${localAccountsError}`
                          : '暂无微信账号，点击“继续登录新微信”开始添加。'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>
      </div>
    </section>
  )
}
